import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useDateLocale } from "@/lib/dateLocale";

export interface DatePillProps {
  date: Date;
  lowestPrice: number | null;
  tripCount: number;
  isSelected: boolean;
  onClick: () => void;
}

/**
 * Paper-ticket date pill.
 * Hairline border, tabular numerals, subtle selected state via ink fill.
 * No more orange dashed borders — that was the AI-slop tell.
 */
export function DatePill({
  date,
  lowestPrice,
  tripCount,
  isSelected,
  onClick,
}: DatePillProps) {
  const dl = useDateLocale();
  const dayAbbrev = format(date, "EEE", { locale: dl.locale }).toUpperCase();
  const dayNum = format(date, "d");
  const monthAbbrev = format(date, "MMM", { locale: dl.locale }).toUpperCase();
  const hasTrips = tripCount > 0 && lowestPrice !== null;

  return (
    <button
      onClick={onClick}
      className={cn(
        "group min-w-[96px] h-[104px] flex flex-col items-center justify-between py-3 px-4 border transition-colors focus-visible-ring text-center",
        "touch-manipulation tabular",
        isSelected
          ? "bg-ink text-stone-50 border-ink"
          : hasTrips
            ? "bg-white border-hairline-strong text-ink hover:border-ink"
            : "bg-white border-hairline text-ink-subtle hover:border-hairline-strong",
      )}
      aria-label={`${format(date, dl.pattern("weekdayLongDate"), { locale: dl.locale })}${
        hasTrips ? `, from $${lowestPrice}` : ", no trips"
      }`}
      aria-pressed={isSelected}
    >
      <span
        className={cn(
          "text-eyebrow uppercase",
          isSelected ? "text-stone-50/70" : "text-ink-subtle",
        )}
      >
        {dayAbbrev}
      </span>

      <div className="flex items-baseline gap-1">
        <span
          className={cn(
            "font-display text-[1.75rem] leading-none",
            isSelected ? "text-stone-50" : "text-ink",
          )}
        >
          {dayNum}
        </span>
        <span
          className={cn(
            "text-[0.7rem] uppercase",
            isSelected ? "text-stone-50/70" : "text-ink-subtle",
          )}
        >
          {monthAbbrev}
        </span>
      </div>

      {hasTrips ? (
        <span
          className={cn(
            "text-[0.8rem] font-medium tabular",
            isSelected ? "text-stone-50" : "text-ink",
          )}
        >
          ${lowestPrice}
        </span>
      ) : (
        <span
          className={cn(
            "h-1 w-1 rounded-full",
            isSelected ? "bg-stone-50/40" : "bg-hairline-strong",
          )}
          aria-hidden="true"
        />
      )}
    </button>
  );
}
